import { ClipboardList, HardHat, HandshakeIcon, Wrench } from "lucide-react";
import Seo from "@/components/Seo";
import Hero from "@/components/Hero";
import { PAGE_META } from "@/lib/seo-constants";
import { breadcrumbSchema } from "@/lib/jsonld";

const PROCESS = [
  {
    icon: ClipboardList,
    title: "Plan",
    text: "We scope the work, walk the site, and build a schedule and budget before a single brick is laid.",
  },
  {
    icon: Wrench,
    title: "Build",
    text: "Our masonry and siding crews execute the trade work directly, coordinated by our own site supervision.",
  },
  {
    icon: HardHat,
    title: "Manage",
    text: "Trades, inspections, and timelines are tracked continuously so nothing slips through the cracks.",
  },
  {
    icon: HandshakeIcon,
    title: "Deliver",
    text: "A final walkthrough and clean handover — the job finished the way it was scoped.",
  },
];

const VALUES = [
  { title: "Safety", text: "Every site run to code, every crew trained and equipped." },
  { title: "Quality", text: "Trade work built to last, not just to pass inspection." },
  { title: "Accountability", text: "One team responsible from the first estimate to the final walkthrough." },
];

export default function About() {
  const meta = PAGE_META.about;

  return (
    <>
      <Seo
        title={meta.title}
        description={meta.description}
        path={meta.path}
        jsonLd={[
          breadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "About", path: "/about" },
          ]),
        ]}
      />

      <Hero
        image="/images/about-team.jpg"
        imageAlt="Construction professionals reviewing architectural drawings in an office"
        eyebrow="About VCO Group"
        title="Masonry & Siding Specialists, Built For Commercial Delivery"
        subtitle="Based in Mississauga, VCO Group is a commercial and industrial construction company built around a trade specialty — masonry and siding — and the project management to deliver it at scale."
        ctaLabel={null}
        size="small"
      />

      <section className="py-20">
        <div className="section-container">
          <div className="mx-auto max-w-3xl text-center">
            <h2 className="font-display text-3xl font-semibold text-charcoal sm:text-4xl">
              Trade Expertise First
            </h2>
            <p className="mt-4 text-steel">
              VCO Group was built around a simple idea: the best commercial
              construction outcomes come from contractors who genuinely know
              the trades they deliver. Our core specialty is masonry and
              bricklaying, paired with siding and exterior cladding — the
              building envelope work that defines how a commercial or
              industrial property performs and looks for decades. We back
              that trade expertise with full project management, so clients
              get one accountable partner from pre-construction planning
              through general contracting, design-build coordination, and
              final delivery.
            </p>
          </div>
        </div>
      </section>

      <section className="bg-white py-20">
        <div className="section-container">
          <h2 className="text-center font-display text-3xl font-semibold text-charcoal sm:text-4xl">
            How We Work
          </h2>
          <div className="mt-12 grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-4">
            {PROCESS.map((step) => (
              <div key={step.title} className="text-center">
                <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-brick/10 text-brick">
                  <step.icon size={26} />
                </div>
                <h3 className="mt-4 font-display text-lg font-semibold text-charcoal">
                  {step.title}
                </h3>
                <p className="mt-2 text-sm text-steel">{step.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20">
        <div className="section-container grid grid-cols-1 gap-8 sm:grid-cols-3">
          {VALUES.map((value) => (
            <div
              key={value.title}
              className="rounded-lg bg-white p-8 text-center shadow-sm ring-1 ring-black/5"
            >
              <h3 className="font-display text-xl font-semibold text-charcoal">
                {value.title}
              </h3>
              <p className="mt-2 text-sm text-steel">{value.text}</p>
            </div>
          ))}
        </div>
      </section>
    </>
  );
}
